import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authFetch } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // A NON-SENSITIVE hint written on login/register and cleared on logout.
  // Its purpose is purely to skip the initial /me + /refresh probe (and the
  // resulting benign 401s in the browser DevTools) when we have no reason to
  // believe there is an active session. The actual auth state still comes
  // from the httpOnly cookies — this flag is not a source of truth.
  const HAS_SESSION_KEY = 'heliophant-has-session';
  const markSession = () => { try { localStorage.setItem(HAS_SESSION_KEY, '1'); } catch { /* noop */ } };
  const clearSessionMark = () => { try { localStorage.removeItem(HAS_SESSION_KEY); } catch { /* noop */ } };

  const checkAuth = useCallback(async () => {
    // Fast path: nothing suggests a session exists → skip both probes.
    // Users can still sign in via /login; that path sets the hint on success.
    let hint = null;
    try { hint = localStorage.getItem(HAS_SESSION_KEY); } catch { /* noop */ }
    if (!hint) {
      setUser(false);
      setLoading(false);
      return;
    }

    try {
      const res = await authFetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else if (res.status === 401) {
        const refreshRes = await authFetch('/api/auth/refresh', { method: 'POST' });
        if (refreshRes.ok) {
          const retryRes = await authFetch('/api/auth/me');
          if (retryRes.ok) {
            setUser(await retryRes.json());
            return;
          }
        }
        clearSessionMark();
        setUser(false);
      } else {
        clearSessionMark();
        setUser(false);
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const res = await authFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw data;
    markSession();
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const res = await authFetch('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw data;
    markSession();
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await authFetch('/api/auth/logout', { method: 'POST' });
    clearSessionMark();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, checkAuth }),
    [user, loading, login, register, logout, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
